import React, { useState, useEffect } from 'react'
import {
  Box,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  Typography,
  Paper,
  alpha,
  Chip
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import api from '../../../services/api'
import { useSnackbar } from 'notistack'
import PatientSearchDialog from './PatientSearchDialog'
import SearchIcon from '@mui/icons-material/Search'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import MedicalServicesIcon from '@mui/icons-material/MedicalServices'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'

const MRDFileForm = ({ onSubmit, onCancel, initialData }) => {
  const { enqueueSnackbar } = useSnackbar()

  const [formData, setFormData] = useState({
    ipdNumber: '',
    uhid: '',
    patientName: '',
    age: '',
    gender: '',
    admissionDate: null,
    dischargeDate: null,
    wardId: '',
    doctorId: '',
    dischargeSummaryId: '',
    priority: 'MEDIUM'
  })

  const [wards, setWards] = useState([])
  const [doctors, setDoctors] = useState([])
  const [loading, setLoading] = useState(false)
  const [wardLoading, setWardLoading] = useState(false)
  const [doctorLoading, setDoctorLoading] = useState(false)
  const [dataLoaded, setDataLoaded] = useState(false)
  const [searchDialogOpen, setSearchDialogOpen] = useState(false)
  const [isCreateMode, setIsCreateMode] = useState(!initialData)

  const parseDate = (dateValue) => {
    if (!dateValue) return null

    try {
      const date = new Date(dateValue)
      return isNaN(date.getTime()) ? null : date
    } catch (error) {
      console.error('Date parsing error:', error)
      return null
    }
  }

  useEffect(() => {
    if (initialData) {
      console.log('Initial data received:', initialData)

      const processedData = {
        ipdNumber: initialData.ipdNumber || '',
        uhid: initialData.uhid || '',
        patientName: initialData.patientName || '',
        age: initialData.age || '',
        gender: initialData.gender || '',
        admissionDate: parseDate(initialData.admissionDate),
        dischargeDate: parseDate(initialData.dischargeDate),
        wardId: initialData.ward?._id || initialData.wardId || '',
        doctorId: initialData.doctor?._id || initialData.doctorId || '',
        dischargeSummaryId: initialData.dischargeSummaryId || '',
        priority: initialData.priority || 'MEDIUM'
      }

      console.log('Processed data:', processedData)
      setFormData(processedData)
      setIsCreateMode(false)  // Edit mode
    } else {
      setIsCreateMode(true)  // Create mode
    }

    loadWards()
    loadDoctors()
    setDataLoaded(true)
  }, [initialData])

  const handlePatientSelect = (patient) => {
    console.log('Selected patient:', patient)

    const calculateAge = (dob) => {
      if (!dob) return ''
      const birthDate = new Date(dob)
      const today = new Date()
      let age = today.getFullYear() - birthDate.getFullYear()
      const monthDiff = today.getMonth() - birthDate.getMonth()
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--
      }
      return age.toString()
    }

    const newFormData = {
      ipdNumber: patient.ipd_no || '',
      uhid: patient.uhid || '',
      patientName: patient.patient?.name || '',
      age: calculateAge(patient.patient?.dob),
      gender: patient.patient?.gender?.toUpperCase() || 'MALE',
      admissionDate: parseDate(patient.date),
      dischargeDate: patient.discharge_date ? parseDate(patient.discharge_date) : parseDate(new Date().toISOString()),
      wardId: patient.consultant_detail?.room?.ward?._id || '',
      doctorId: patient.consultant_detail?.doctor?._id || '',
      dischargeSummaryId: '',
      priority: 'MEDIUM'
    }

    if (patient.consultant_detail?.doctor?._id && doctors.length > 0) {
      const foundDoctor = doctors.find(doc => doc._id === patient.consultant_detail.doctor._id)
      if (foundDoctor) {
        newFormData.doctorId = foundDoctor._id
      } else {
        enqueueSnackbar('Doctor not found in system. Please select manually.', { variant: 'warning' })
      }
    }

    if (patient.consultant_detail?.room?.ward?._id && wards.length > 0) {
      const foundWard = wards.find(ward => ward._id === patient.consultant_detail.room.ward._id)
      if (foundWard) {
        newFormData.wardId = foundWard._id
      } else {
        enqueueSnackbar('Ward not found in system. Please select manually.', { variant: 'warning' })
      }
    }

    setFormData(newFormData)
    enqueueSnackbar('Patient data loaded successfully', { variant: 'success' })
  }

  const loadWards = async () => {
    setWardLoading(true)
    try {
      const response = await api.get('/master/wards/active')
      setWards(response.data.data || [])
    } catch (error) {
      console.error('Failed to load wards:', error)
      enqueueSnackbar('Failed to load wards', { variant: 'error' })
    } finally {
      setWardLoading(false)
    }
  }

  const loadDoctors = async () => {
    setDoctorLoading(true)
    try {
      const response = await api.get('/master/doctors/active')
      setDoctors(response.data.data || [])
    } catch (error) {
      console.error('Failed to load doctors:', error)
      enqueueSnackbar('Failed to load doctors', { variant: 'error' })
    } finally {
      setDoctorLoading(false)
    }
  }

  const handleChange = (field, value) => {
    if (isCreateMode && formData[field] && ['ipdNumber', 'uhid', 'patientName', 'admissionDate'].includes(field)) {
      enqueueSnackbar('This field is auto-filled from patient search. Use patient search to change.', { variant: 'warning' })
      return
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      enqueueSnackbar('Please fill all required fields', { variant: 'error' })
      return
    }
    
    setLoading(true)
    try {
      await onSubmit(formData)
    } catch (error) {
      enqueueSnackbar('Failed to save MRD file', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const validateForm = () => {
    const required = ['ipdNumber', 'uhid', 'patientName', 'dischargeDate', 'wardId', 'doctorId']
    
    if (isCreateMode && !formData.uhid) {
      return false
    }
    
    return required.every(field => {
      const value = formData[field]
      return value !== '' && value !== null && value !== undefined
    })
  }

  const isFormReadyForSubmit = () => {
    if (isCreateMode) {
      return formData.uhid && formData.uhid.trim() !== ''
    }
    return validateForm()
  }

  if (!dataLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Paper 
        elevation={0} 
        sx={{ 
          p: 3, 
          borderRadius: 2,
          border: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper'
        }}
      >
        <Box component="form" onSubmit={handleSubmit}>
          <Box sx={{ mb: 3 }}>
            <Alert 
              severity="info" 
              icon={<MedicalServicesIcon />}
              sx={{ 
                borderRadius: 1,
                mb: 3
              }}
            >
              {initialData
                ? 'Update the MRD file information below. Changes will be saved immediately.'
                : 'Search for a patient to auto-fill all details. Only patients with admission records will be shown.'
              }
            </Alert>

            {isCreateMode && (
              <Paper 
                elevation={0}
                sx={{ 
                  p: 3, 
                  borderRadius: 2,
                  border: 1,
                  borderColor: 'divider',
                  bgcolor: alpha('#1976d2', 0.03),
                  mb: 3
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <PersonAddIcon color="primary" />
                  <Typography variant="h6" color="primary">
                    Patient Selection
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<SearchIcon />}
                    onClick={() => setSearchDialogOpen(true)}
                    sx={{ 
                      py: 1.5,
                      px: 4,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    {formData.uhid ? 'Change Patient Selection' : 'Search Patient'}
                  </Button>
                  
                  {formData.uhid && (
                    <Box sx={{ ml: 2 }}>
                      <Chip
                        label={`Selected: ${formData.patientName}`}
                        color="success"
                        variant="outlined"
                        sx={{ fontWeight: 500 }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        UHID: {formData.uhid}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            )}
          </Box>

          <Grid container spacing={2.5}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1, 
                mb: 2,
                pb: 1,
                borderBottom: 2,
                borderColor: 'primary.main'
              }}>
                <CalendarTodayIcon color="primary" />
                <Typography variant="subtitle1" fontWeight={600} color="primary">
                  Patient Information
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="IPD Number"
                value={formData.ipdNumber}
                onChange={(e) => handleChange('ipdNumber', e.target.value)}
                placeholder="e.g., IP-7-25"
                helperText="Unique IPD number from HIS"
                disabled={isCreateMode}
                InputProps={{
                  readOnly: isCreateMode,
                }}
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="UHID"
                value={formData.uhid}
                onChange={(e) => handleChange('uhid', e.target.value)}
                placeholder="e.g., GH-0018"
                helperText="Unique Hospital Identification Number"
                disabled={isCreateMode}
                InputProps={{
                  readOnly: isCreateMode,
                }}
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={8}>
              <TextField
                required
                fullWidth
                label="Patient Name"
                value={formData.patientName}
                onChange={(e) => handleChange('patientName', e.target.value)}
                disabled={isCreateMode}
                InputProps={{
                  readOnly: isCreateMode,
                }}
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Age"
                type="number"
                value={formData.age}
                onChange={(e) => handleChange('age', e.target.value)}
                disabled={isCreateMode}
                InputProps={{
                  readOnly: isCreateMode,
                }}
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Gender</InputLabel>
                <Select
                  value={formData.gender}
                  label="Gender"
                  onChange={(e) => handleChange('gender', e.target.value)}
                  disabled={isCreateMode}
                >
                  <MenuItem value="MALE">Male</MenuItem>
                  <MenuItem value="FEMALE">Female</MenuItem>
                  <MenuItem value="OTHER">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Dates */}
            <Grid item xs={12} md={6}>
              <DatePicker
                label="Admission Date"
                value={formData.admissionDate}
                onChange={(value) => handleChange('admissionDate', value)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: false,
                    readOnly: isCreateMode,
                    size: 'small'
                  }
                }}
                readOnly={isCreateMode}
                disabled={isCreateMode}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <DatePicker
                required
                label="Discharge Date"
                value={formData.dischargeDate}
                onChange={(value) => handleChange('dischargeDate', value)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: false,
                    size: 'small'
                  }
                }}
              />
            </Grid>

            {/* Ward & Doctor */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Ward *</InputLabel>
                <Select
                  required
                  value={formData.wardId}
                  label="Ward *"
                  onChange={(e) => handleChange('wardId', e.target.value)}
                  disabled={wardLoading}
                >
                  <MenuItem value="">
                    <em>Select Ward</em>
                  </MenuItem>
                  {wards.map((ward) => (
                    <MenuItem key={ward._id} value={ward._id}>
                      {ward.name} ({ward.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Doctor *</InputLabel>
                <Select
                  required
                  value={formData.doctorId}
                  label="Doctor *"
                  onChange={(e) => handleChange('doctorId', e.target.value)}
                  disabled={doctorLoading}
                >
                  <MenuItem value="">
                    <em>Select Doctor</em>
                  </MenuItem>
                  {doctors.map((doctor) => (
                    <MenuItem key={doctor._id} value={doctor._id}>
                      Dr. {doctor.name} {doctor.specialization ? `(${doctor.specialization})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Priority */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  label="Priority"
                  onChange={(e) => handleChange('priority', e.target.value)}
                >
                  <MenuItem value="HIGH">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main' }} />
                      <Typography variant="body2">High Priority</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="MEDIUM">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'warning.main' }} />
                      <Typography variant="body2">Medium Priority</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="LOW">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main' }} />
                      <Typography variant="body2">Low Priority</Typography>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Remarks */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Remarks (Optional)"
                placeholder="Any special instructions or notes..."
                size="small"
              />
            </Grid>
          </Grid>

          {/* Actions */}
          <Box sx={{ 
            mt: 4, 
            pt: 3, 
            borderTop: 1, 
            borderColor: 'divider',
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: 2 
          }}>
            <Button 
              onClick={onCancel} 
              disabled={loading}
              variant="outlined"
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                px: 4
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!isFormReadyForSubmit() || loading}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                px: 4,
                fontWeight: 600
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                initialData ? 'Update MRD File' : 'Create MRD File'
              )}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Patient Search Dialog */}
      <PatientSearchDialog
        open={searchDialogOpen}
        onClose={() => setSearchDialogOpen(false)}
        onSelectPatient={handlePatientSelect}
      />
    </LocalizationProvider>
  )
}

export default MRDFileForm